import React, { useState, useEffect } from "react";
import {
    AreaChart,
    Area,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    Label,
} from "recharts";
import { Menu, ChevronDown } from "lucide-react";
import { DashboardService } from "../../services/dashboard";
import Dropdown from "./dropdown";

const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleString('default', { month: 'short' });
};

const AdminAnalytics = ({ stats, restaurants }) => {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [timeframe, setTimeframe] = useState(null);

    const timeframeOptions = [
        { value: "30d", label: "Last 30 Days" },
        { value: "90d", label: "Last 90 Days" },
        { value: "6m", label: "Last 6 Months" },
        { value: "12m", label: "Last 12 Months" },
    ];

    const fetchAnalytics = async (selectedTimeframe) => {
        try {
            setLoading(true);
            const data = await DashboardService.getSuperAdminAnalytics(selectedTimeframe);
            setAnalyticsData(data);
            if (!timeframe && data.timeframe) {
                setTimeframe(data.timeframe);
            }
        } catch (error) {
            console.error("Error fetching admin analytics:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics(timeframe);
    }, [timeframe]);

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12 bg-white rounded-[24px] shadow-sm border border-gray-100 mb-8 min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-500"></div>
            </div>
        );
    }

    if (!analyticsData) return null;

    const {
        restaurantGrowth,
        userGrowth,
        lessonCompletions,
        sessionDuration,
        trainingEffectiveness,
        performanceRanking
    } = analyticsData;

    // Transform data for charts
    const formattedRestaurantGrowth = restaurantGrowth.map(item => ({
        name: formatMonth(item.month),
        value: item.count
    }));

    const formattedUserGrowth = userGrowth.map(item => ({
        name: formatMonth(item.month),
        EMPLOYEES: item.EMPLOYEES,
        MANAGERS: item.MANAGERS,
        DIRECTORS: item.DIRECTORS
    }));

    const formattedCompletions = lessonCompletions.map(item => ({
        name: formatMonth(item.month),
        value: item.count
    }));

    const formattedDuration = sessionDuration.map(item => ({
        name: formatMonth(item.month),
        value: item.duration
    }));

    return (
        <div className="flex flex-col xl:flex-row gap-6 mb-8 ">
            {/* Charts Section */}
            <div className="flex-1 bg-white rounded-[24px] p-6 sm:p-8 shadow-sm border border-gray-100">
                <div className="flex justify-between items-center mb-10">
                    <h2 className="text-2xl font-black text-[#1F2937] tracking-tighter">
                        Company Growth & Usage Analytics
                    </h2>
                    <div className="flex items-center gap-2">
                        <Dropdown
                            label={<Menu size={20} className="text-gray-500" />}
                            options={timeframeOptions}
                            selectedOption={timeframe}
                            onSelect={(opt) => setTimeframe(opt.value)}
                            buttonClassName="p-2.5 bg-gray-50 border-gray-100 hover:bg-gray-100 rounded-xl transition-all"
                            showSelectedLabel={false}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-12">
                    {/* Restaurant Growth */}
                    <div className="h-[220px] flex flex-col">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-[14px] font-semibold text-[#111827] tracking-tight ">Restaurant Growth Over Time</h3>

                        </div>
                        <ResponsiveContainer width="100%" height="100%" className="outline-none focus:outline-none">
                            <AreaChart data={formattedRestaurantGrowth} style={{ outline: 'none' }} accessibilityLayer={false}>
                                <defs>
                                    <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#881337" stopOpacity={0.1} />
                                        <stop offset="95%" stopColor="#881337" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 900, fill: '#9CA3AF' }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#9CA3AF' }} />
                                <Tooltip />
                                <Area type="monotone" dataKey="value" stroke="#881337" strokeWidth={2} fillOpacity={1} fill="url(#colorGrowth)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Active Users */}
                    <div className="h-[220px] flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-[14px] font-semibold text-[#111827] tracking-tight ">Active Users Over Time</h3>

                        </div>
                        <ResponsiveContainer width="100%" height="100%" className="outline-none focus:outline-none">
                            <LineChart data={formattedUserGrowth} style={{ outline: 'none' }} accessibilityLayer={false}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#9CA3AF' }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#9CA3AF' }} />
                                <Tooltip />
                                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', paddingTop: '20px', letterSpacing: '0.1em' }} />
                                <Line type="monotone" dataKey="EMPLOYEES" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3, fill: '#3B82F6' }} />
                                <Line type="monotone" dataKey="MANAGERS" stroke="#10B981" strokeWidth={2} dot={{ r: 3, fill: '#10B981' }} />
                                <Line type="monotone" dataKey="DIRECTORS" stroke="#92400E" strokeWidth={2} dot={{ r: 3, fill: '#92400E' }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Lesson Completions */}
                    <div className="h-[260px] flex flex-col">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-[14px] font-semibold text-[#111827] tracking-tight ">Lesson Completions Over Time</h3>

                        </div>
                        <ResponsiveContainer width="100%" height="100%" className="outline-none focus:outline-none">
                            <AreaChart data={formattedCompletions} margin={{ left: 15, right: 0, top: 0, bottom: 20 }} style={{ outline: 'none' }} accessibilityLayer={false}>
                                <defs>
                                    <linearGradient id="colorCompletions" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#FB923C" stopOpacity={0.15} />
                                        <stop offset="95%" stopColor="#FB923C" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 11, fontWeight: 700, fill: '#9CA3AF' }}
                                    dy={10}
                                    angle={-45}
                                    textAnchor="end"
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 11, fontWeight: 700, fill: '#9CA3AF' }}
                                    tickFormatter={(value) => value >= 1000 ? `${value / 1000}k` : value}
                                    width={45}
                                >
                                    <Label
                                        value="Cumulative Completions"
                                        angle={-90}
                                        position="insideLeft"
                                        offset={-10}
                                        style={{ textAnchor: 'middle', fontSize: '11px', fontWeight: 800, fill: '#9CA3AF', textTransform: 'none', fontFamily: 'Inter' }}
                                    />
                                </YAxis>
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 16px -4px rgb(0 0 0 / 0.1)', fontFamily: 'Inter' }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#F97316"
                                    strokeWidth={3}
                                    fillOpacity={1}
                                    fill="url(#colorCompletions)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Session Duration */}
                    <div className="h-[220px] flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-[14px] font-semibold text-[#111827] tracking-tight">Avg. Session Duration (min)</h3>

                        </div>
                        <ResponsiveContainer width="100%" height="100%" className="outline-none focus:outline-none">
                            <BarChart data={formattedDuration} style={{ outline: 'none' }} accessibilityLayer={false}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#9CA3AF' }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#9CA3AF' }} />
                                <Tooltip />
                                <Bar dataKey="value" fill="#FDA4AF" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Summary Section */}
            <div className="w-full xl:w-[400px] bg-white rounded-[24px] p-6 lg:p-8 shadow-sm border border-gray-100 flex flex-col">
                <h2 className="text-lg font-black text-[#1F2937] tracking-tighter mb-8">
                    Training Effectiveness Summary
                </h2>

                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-[#FFF1F2] p-6 rounded-[20px] border border-rose-100 text-center shadow-sm">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Total Lessons Completed</p>
                        <p className="text-2xl font-semibold text-[#881337]">{trainingEffectiveness.totalLessonsCompleted.toLocaleString()}</p>
                    </div>
                    <div className="bg-[#EFF6FF] p-6 rounded-[20px] border border-blue-100 text-center shadow-sm">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Average Quiz Score</p>
                        <p className="text-2xl font-semibold text-blue-600">{trainingEffectiveness.averageQuizScore.toFixed(1)}%</p>
                    </div>
                </div>

                <div className="bg-gray-50/50 p-6 rounded-[20px] border border-gray-100 text-center mb-8">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Average Attempts per Lesson</p>
                    <p className="text-4xl font-semibold text-[#1F2937]">{trainingEffectiveness.averageAttemptsPerLesson}</p>
                </div>

                <div className="space-y-4 text-start">
                    <div className="p-5 rounded-[20px] bg-[#ECFDF5] border border-emerald-100 flex items-center justify-between shadow-sm">
                        <div className="flex-1">
                            <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-[0.2em] mb-1">Top Performing</p>
                            <p className="text-[15px] font-semibold text-[#111827]">{performanceRanking.topPerforming?.name || "N/A"}</p>
                        </div>
                        <span className="text-xl font-semibold text-emerald-500">{performanceRanking.topPerforming ? Math.round(performanceRanking.topPerforming.avgScore) : 0}%</span>
                    </div>

                    <div className="p-5 rounded-[20px] bg-[#FFF7ED] border border-orange-100 flex items-center justify-between shadow-sm">
                        <div className="flex-1">
                            <p className="text-[10px] font-semibold text-orange-600 uppercase tracking-[0.2em] mb-1">Lowest Performing</p>
                            <p className="text-[15px] font-semibold text-[#111827]">{performanceRanking.lowestPerforming?.name || "N/A"}</p>
                        </div>
                        <span className="text-xl font-semibold text-orange-500">{performanceRanking.lowestPerforming ? Math.round(performanceRanking.lowestPerforming.avgScore) : 0}%</span>
                    </div>

                    <div className="p-5 rounded-[20px] bg-[#FFFBEB] border border-amber-100 flex items-center justify-between shadow-sm">
                        <div className="flex-1">
                            <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-[0.2em] mb-1">Most Improved (30d)</p>
                            <p className="text-[15px] font-semibold text-[#111827]">{performanceRanking.mostImproved?.name || "N/A"}</p>
                        </div>
                        <span className="text-xl font-semibold text-amber-500">+{performanceRanking.mostImproved?.improvement || 0}%</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminAnalytics;
